// Utility functions for handling media storage and blob URL persistence

import { Slide } from '../types';

// Blob URL 캐시 관리
const blobUrlCache = new Map<Blob, string>();

// Convert blob URL to Blob object
export const convertBlobUrlToBlob = async (blobUrl: string): Promise<Blob | null> => {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Failed to fetch blob from URL:', error);
    return null;
  }
};

// Create blob URL from Blob object with caching
export const createBlobUrlFromBlob = (blob: Blob): string => {
  // Check if we already have a URL for this blob
  if (blobUrlCache.has(blob)) {
    return blobUrlCache.get(blob)!;
  }
  
  const url = URL.createObjectURL(blob);
  blobUrlCache.set(blob, url);
  return url;
};

// Clean up blob URLs
export const revokeBlobUrl = (url: string) => {
  // Find and remove from cache
  for (const [blob, cachedUrl] of blobUrlCache.entries()) {
    if (cachedUrl === url) {
      blobUrlCache.delete(blob);
      URL.revokeObjectURL(url);
      break;
    }
  }
};

// Clean up all cached blob URLs
export const revokeAllBlobUrls = () => {
  for (const url of blobUrlCache.values()) {
    URL.revokeObjectURL(url);
  }
  blobUrlCache.clear();
};

// Process slides for saving (convert blob URLs to Blob objects)
export const processSlidesForSaving = async (slides: Slide[]): Promise<Slide[]> => {
  const processedSlides = await Promise.all(slides.map(async (slide) => {
    const processedSlide = { ...slide };
    
    // Process background image
    if (slide.backgroundImage && slide.backgroundImage.startsWith('blob:') && !slide.backgroundImageBlob) {
      const blob = await convertBlobUrlToBlob(slide.backgroundImage);
      if (blob) {
        processedSlide.backgroundImageBlob = blob;
      }
    }
    
    // Process elements
    if (slide.elements) {
      processedSlide.elements = await Promise.all(slide.elements.map(async (element) => {
        if ((element.type === 'image' || element.type === 'video') && 
            element.content && element.content.startsWith('blob:') && !element.contentBlob) {
          const blob = await convertBlobUrlToBlob(element.content);
          if (blob) {
            return {
              ...element,
              contentBlob: blob
            };
          }
        }
        return element;
      }));
    }
    
    return processedSlide;
  }));
  
  return processedSlides;
};

// Process slides after loading (create new blob URLs from Blob objects)
export const processSlidesAfterLoading = (slides: Slide[]): Slide[] => {
  return slides.map(slide => {
    const processedSlide = { ...slide };
    
    // Process background image
    if (slide.backgroundImageBlob) {
      processedSlide.backgroundImage = createBlobUrlFromBlob(slide.backgroundImageBlob);
    }
    
    // Process elements
    if (slide.elements) {
      processedSlide.elements = slide.elements.map(element => {
        if ((element.type === 'image' || element.type === 'video') && element.contentBlob) {
          return {
            ...element,
            content: createBlobUrlFromBlob(element.contentBlob)
          };
        }
        return element;
      });
    }
    
    return processedSlide;
  });
};

// Clean up blob URLs when slides are removed
export const cleanupBlobUrls = (slides: Slide[]) => {
  slides.forEach(slide => {
    // Clean up background image
    if (slide.backgroundImage && slide.backgroundImage.startsWith('blob:')) {
      URL.revokeObjectURL(slide.backgroundImage);
    }
    
    // Clean up element media
    if (slide.elements) {
      slide.elements.forEach(element => {
        if ((element.type === 'image' || element.type === 'video') && 
            element.content && element.content.startsWith('blob:')) {
          URL.revokeObjectURL(element.content);
        }
      });
    }
  });
};